import { useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Download,
  Search,
  Target,
  TrendingUp,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useDebounce } from "@/hooks/useDebounce";
import { toast } from "@/hooks/use-toast";
import {
  commercialCyclesApi,
  commercialRevenueApi,
} from "@/lib/api";
import { formatCnpj, formatCurrency, formatPercentage } from "@/lib/formatters";
import type { CommercialRevenueSortDirection } from "@/types/commercialRevenue";
import type { CommercialRevenueSortField } from "@/types/commercialRevenue";

const PAGE_SIZES = [10, 25, 50];

const percentageBadgeClass = (value: number) => {
  if (value >= 100) return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (value >= 75) return "border-blue-200 bg-blue-50 text-blue-700";
  if (value > 0) return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-border bg-muted text-muted-foreground";
};

export default function CommercialRevenueDashboard() {
  const navigate = useNavigate();
  const [cycleYear, setCycleYear] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const [sortBy, setSortBy] = useState<CommercialRevenueSortField>("forecastAmount");
  const [sortDirection, setSortDirection] = useState<CommercialRevenueSortDirection>("desc");
  const [isExporting, setIsExporting] = useState(false);
  const debouncedSearch = useDebounce(search, 300);

  const cyclesQuery = useQuery({
    queryKey: ["commercial-revenue-cycles"],
    queryFn: () => commercialCyclesApi.list(),
    staleTime: 60_000,
  });

  const cycles = useMemo(
    () => [...(cyclesQuery.data?.data ?? [])].sort((left, right) => right.year - left.year),
    [cyclesQuery.data],
  );

  useEffect(() => {
    if (cycleYear !== null || cycles.length === 0) return;
    const currentCycle = cycles.find((cycle) => cycle.status === "Open") ?? cycles[0];
    setCycleYear(currentCycle.year);
  }, [cycleYear, cycles]);

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  const revenueQuery = useQuery({
    queryKey: [
      "commercial-revenue",
      cycleYear,
      pageNumber,
      pageSize,
      sortBy,
      sortDirection,
      debouncedSearch,
    ],
    queryFn: () => commercialRevenueApi.get({
      cycleYear: cycleYear!,
      pageNumber,
      pageSize,
      sortBy,
      sortDirection,
      search: debouncedSearch,
    }),
    enabled: cycleYear !== null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const result = revenueQuery.data;
  const firstVisible = result && result.totalCount > 0
    ? (result.pageNumber - 1) * result.pageSize + 1
    : 0;
  const lastVisible = result
    ? Math.min(result.pageNumber * result.pageSize, result.totalCount)
    : 0;

  useEffect(() => {
    if (result && result.totalPages > 0 && pageNumber > result.totalPages) {
      setPageNumber(result.totalPages);
    }
  }, [pageNumber, result]);

  const changeCycle = (value: string) => {
    setCycleYear(Number(value));
    setPageNumber(1);
  };

  const toggleSort = (field: CommercialRevenueSortField) => {
    if (sortBy === field) {
      setSortDirection((current) => current === "desc" ? "asc" : "desc");
    } else {
      setSortBy(field);
      setSortDirection("desc");
    }
    setPageNumber(1);
  };

  const sortIcon = (field: CommercialRevenueSortField) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortDirection === "desc"
      ? <ArrowDown className="h-4 w-4" />
      : <ArrowUp className="h-4 w-4" />;
  };

  // Exporta todos os clientes do ciclo (sem paginação), com a busca e a ordenação aplicadas na tabela.
  const handleExport = async () => {
    if (cycleYear === null) return;
    setIsExporting(true);
    try {
      const blob = await commercialRevenueApi.exportXlsx({
        cycleYear,
        sortBy,
        sortDirection,
        search: debouncedSearch,
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `faturamento-clientes-${cycleYear}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast({
        title: "Não foi possível exportar o faturamento",
        description: "Tente novamente em instantes.",
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <BarChart3 className="h-6 w-6 text-primary" aria-hidden="true" />
            <h1 className="text-2xl font-bold tracking-tight">Faturamento por cliente</h1>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
            Consulte o previsto e realizado por cliente ativo e ciclo comercial selecionado.
          </p>
        </div>

        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-end">
          <div className="w-full sm:w-56">
            <label htmlFor="commercial-cycle" className="mb-1.5 block text-sm font-medium">
              Ciclo comercial
            </label>
            <Select
              value={cycleYear?.toString() ?? ""}
              onValueChange={changeCycle}
              disabled={cyclesQuery.isLoading || cycles.length === 0}
            >
              <SelectTrigger id="commercial-cycle">
                <SelectValue placeholder="Selecione o ciclo" />
              </SelectTrigger>
              <SelectContent>
                {cycles.map((cycle) => (
                  <SelectItem key={cycle.cycleId} value={cycle.year.toString()}>
                    Ciclo {cycle.year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            variant="outline"
            onClick={handleExport}
            disabled={isExporting || !result || result.totalCount === 0}
          >
            <Download className="mr-2 h-4 w-4" />
            {isExporting ? "Exportando..." : "Exportar Excel"}
          </Button>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-primary/10 p-2.5 text-primary"><Users className="h-5 w-5" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Clientes com perfil ativo</p>
              {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-20" /> : <p className="text-2xl font-bold">{result?.totalCount ?? 0}</p>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-blue-50 p-2.5 text-blue-700"><Target className="h-5 w-5" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Projeção Total do ciclo</p>
              {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-36" /> : <p className="text-xl font-bold">{formatCurrency(result?.totalForecastAmount ?? 0)}</p>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-emerald-50 p-2.5 text-emerald-700"><CircleDollarSign className="h-5 w-5" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Realizado</p>
              {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-36" /> : <p className="text-xl font-bold">{formatCurrency(result?.totalRealizedAmount ?? 0)}</p>}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="flex items-center gap-4 p-5">
            <div className="rounded-lg bg-amber-50 p-2.5 text-amber-700"><TrendingUp className="h-5 w-5" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Percentual realizado</p>
              {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-24" /> : <p className="text-2xl font-bold">{formatPercentage(result?.totalRealizedPercentage ?? 0)}</p>}
            </div>
          </CardContent>
        </Card>
      </div>

      <section className="space-y-3" aria-labelledby="cycle-goal-title">
        <h2 id="cycle-goal-title" className="text-lg font-semibold">Previsto 1º semestre</h2>
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          <Card>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-lg bg-violet-50 p-2.5 text-violet-700"><Target className="h-5 w-5" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Previsto 1º semestre</p>
                {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-36" /> : <p className="text-xl font-bold">{formatCurrency(result?.totalGoalAmount ?? 0)}</p>}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><CircleDollarSign className="h-5 w-5" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Realizado 1º semestre</p>
                {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-36" /> : <p className="text-xl font-bold">{formatCurrency(result?.totalGoalRealizedAmount ?? 0)}</p>}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-5">
              <div className="rounded-lg bg-cyan-50 p-2.5 text-cyan-700"><TrendingUp className="h-5 w-5" /></div>
              <div>
                <p className="text-sm text-muted-foreground">Percentual realizado 1º semestre</p>
                {revenueQuery.isLoading ? <Skeleton className="mt-2 h-7 w-24" /> : <p className="text-2xl font-bold">{formatPercentage(result?.totalGoalRealizedPercentage ?? 0)}</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      <Card>
        <CardHeader className="border-b">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">Clientes do ciclo {cycleYear ?? "—"}</CardTitle>
            <div className="relative w-full sm:w-80">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Buscar por escola ou CNPJ"
                aria-label="Buscar por escola ou CNPJ"
                className="pl-9"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {revenueQuery.isError ? (
            <div className="p-10 text-center">
              <p className="text-sm font-medium text-destructive">Não foi possível carregar os dados de faturamento.</p>
              <Button variant="outline" className="mt-4" onClick={() => revenueQuery.refetch()}>Tentar novamente</Button>
            </div>
          ) : revenueQuery.isLoading && !result ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 6 }).map((_, index) => <Skeleton key={index} className="h-11 w-full" />)}
            </div>
          ) : result?.items.length === 0 ? (
            <div className="p-10 text-center text-sm text-muted-foreground">Nenhum cliente encontrado neste ciclo.</div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[18rem]">Cliente</TableHead>
                      <TableHead className="min-w-[13rem] text-right" aria-sort={sortBy === "forecastAmount" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("forecastAmount")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Projeção Total do ciclo
                          {sortIcon("forecastAmount")}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[12rem] text-right" aria-sort={sortBy === "realizedAmount" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("realizedAmount")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Realizado
                          {sortIcon("realizedAmount")}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[12rem] text-right" aria-sort={sortBy === "realizedPercentage" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("realizedPercentage")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Percentual realizado
                          {sortIcon("realizedPercentage")}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[12rem] text-right" aria-sort={sortBy === "goalAmount" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("goalAmount")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Previsto 1º semestre
                          {sortIcon("goalAmount")}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[12rem] text-right" aria-sort={sortBy === "goalRealizedAmount" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("goalRealizedAmount")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Realizado 1º semestre
                          {sortIcon("goalRealizedAmount")}
                        </button>
                      </TableHead>
                      <TableHead className="min-w-[12rem] text-right" aria-sort={sortBy === "goalRealizedPercentage" ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
                        <button
                          type="button"
                          onClick={() => toggleSort("goalRealizedPercentage")}
                          className="ml-auto inline-flex items-center gap-1.5 font-medium hover:text-foreground"
                        >
                          Percentual realizado 1º semestre
                          {sortIcon("goalRealizedPercentage")}
                        </button>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {result?.items.map((client) => (
                      <TableRow
                        key={client.clientId}
                        role="link"
                        tabIndex={0}
                        className="cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        onClick={() => navigate(`/backoffice/gestao-comercial/faturamento-clientes/${client.clientId}?cycleYear=${cycleYear}`)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter" || event.key === " ") {
                            event.preventDefault();
                            navigate(`/backoffice/gestao-comercial/faturamento-clientes/${client.clientId}?cycleYear=${cycleYear}`);
                          }
                        }}
                        aria-label={`Ver pedidos de ${client.clientName}`}
                      >
                        <TableCell>
                          <div className="font-medium">{client.clientName}</div>
                          <div className="font-mono text-xs text-muted-foreground">CNPJ {formatCnpj(client.clientCnpj)}</div>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(client.forecastAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(client.realizedAmount)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={`min-w-20 justify-center tabular-nums ${percentageBadgeClass(client.realizedPercentage)}`}>
                            {formatPercentage(client.realizedPercentage)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right font-semibold tabular-nums">{formatCurrency(client.goalAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(client.goalRealizedAmount)}</TableCell>
                        <TableCell className="text-right">
                          <Badge variant="outline" className={`min-w-20 justify-center tabular-nums ${percentageBadgeClass(client.goalRealizedPercentage)}`}>
                            {formatPercentage(client.goalRealizedPercentage)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-wrap items-center gap-2 text-muted-foreground">
                  <span>Linhas por página</span>
                  <Select
                    value={pageSize.toString()}
                    onValueChange={(value) => {
                      setPageSize(Number(value));
                      setPageNumber(1);
                    }}
                  >
                    <SelectTrigger className="h-8 w-20"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAGE_SIZES.map((size) => <SelectItem key={size} value={size.toString()}>{size}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span>{firstVisible}–{lastVisible} de {result?.totalCount ?? 0}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pageNumber <= 1 || revenueQuery.isFetching} onClick={() => setPageNumber((page) => page - 1)}>
                    <ChevronLeft className="mr-1 h-4 w-4" /> Anterior
                  </Button>
                  <span className="whitespace-nowrap text-muted-foreground">Página {result?.pageNumber ?? 0} de {result?.totalPages ?? 0}</span>
                  <Button variant="outline" size="sm" disabled={!result || pageNumber >= result.totalPages || revenueQuery.isFetching} onClick={() => setPageNumber((page) => page + 1)}>
                    Próxima <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
